import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Upload, Trash2, FileText, Download } from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'council_cemetery', label: 'Council cemetery' },
  { value: 'parish_council_cemetery', label: 'Parish council cemetery' },
  { value: 'churchyard', label: 'Churchyard' },
  { value: 'private_cemetery', label: 'Private cemetery' },
  { value: 'natural_burial_ground', label: 'Natural burial ground' },
  { value: 'other', label: 'Other burial authority/site' },
];

const DETAIL_FIELDS = [
  ['name', 'Cemetery / Churchyard name *'],
  ['burial_authority', 'Burial authority'],
  ['address', 'Address'],
  ['postcode', 'Postcode'],
  ['diocese', 'Diocese'],
  ['parish', 'Parish'],
  ['telephone', 'Telephone'],
  ['email', 'General email'],
  ['application_email', 'Memorial application email'],
  ['website', 'Website'],
  ['application_fee', 'Application fee — new memorial (£)'],
  ['application_fee_inscription', 'Application fee — add-on inscription (£)'],
  ['online_application_url', 'Online application URL'],
  ['last_verified_date', 'Last verified date'],
];

const REQ_BOOL_FIELDS = [
  ['own_form_required', 'Own authority form required'],
  ['deed_holder_signature_required', 'Deed holder signature required'],
  ['applicant_signature_required', 'Applicant signature required'],
  ['mason_signature_required', 'Mason signature required'],
  ['grave_number_required', 'Grave/plot number required'],
  ['drawing_required', 'Drawing required'],
  ['photograph_required', 'Photograph required'],
  ['full_inscription_required', 'Full inscription required'],
  ['fixing_method_required', 'Fixing method required'],
  ['bramm_required', 'BRAMM registration required'],
  ['namm_required', 'NAMM registration required'],
  ['bs8415_required', 'BS 8415 compliance required'],
  ['kerb_sets_permitted', 'Kerb sets permitted'],
  ['cover_slabs_permitted', 'Cover slabs permitted'],
  ['vase_memorials_permitted', 'Vase memorials permitted'],
  ['photo_plaques_permitted', 'Photographic plaques permitted'],
  ['polished_granite_permitted', 'Polished granite permitted'],
];

const REQ_TEXT_FIELDS = [
  ['max_height', 'Max height — full size'],
  ['max_width', 'Max width — full size'],
  ['max_thickness', 'Max thickness — full size'],
  ['cremation_max_height', 'Max height — cremation'],
  ['cremation_max_width', 'Max width — cremation'],
  ['cremation_max_thickness', 'Max thickness — cremation'],
  ['children_max_height', "Max height — children's"],
  ['children_max_width', "Max width — children's"],
  ['children_max_thickness', "Max thickness — children's"],
  ['max_base_width', 'Max base width'],
  ['max_base_depth', 'Max base depth'],
  ['material_restrictions', 'Material restrictions'],
  ['colour_restrictions', 'Colour restrictions'],
  ['ornamentation_restrictions', 'Ornamentation restrictions'],
  ['lettering_restrictions', 'Lettering restrictions'],
  ['submission_email', 'Submission email'],
];

const NOTE_FIELDS = [
  ['account_reference', 'Our account/reference with authority'],
  ['contact_name', 'Our contact person'],
  ['contact_phone', 'Contact phone'],
  ['contact_email', 'Contact email'],
  ['payment_arrangements', 'Payment arrangements'],
];

function TriState({ value, onChange, testId }) {
  const v = value === true ? 'yes' : value === false ? 'no' : 'unknown';
  return (
    <Select value={v} onValueChange={(nv) => onChange(nv === 'unknown' ? null : nv === 'yes')}>
      <SelectTrigger className="h-8 w-28" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="unknown">Unknown</SelectItem>
        <SelectItem value="yes">Yes</SelectItem>
        <SelectItem value="no">No</SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function CemeteryFormDialog({ open, onOpenChange, cemetery, canEdit = true, onSaved }) {
  const { token, API } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const isNew = !cemetery;
  const suggestMode = !isNew && !canEdit;

  const [form, setForm] = useState({});
  const [req, setReq] = useState({});
  const [customReqs, setCustomReqs] = useState([]);
  const [notes, setNotes] = useState({});
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadKind, setUploadKind] = useState('application_form');

  useEffect(() => {
    if (!open) return;
    if (cemetery) {
      setForm({ ...cemetery });
      setReq({ ...(cemetery.requirements || {}) });
      setCustomReqs(cemetery.custom_requirements || []);
      axios.get(`${API}/cemeteries/${cemetery.id}`, auth).then((r) => {
        setDocs(r.data.documents || []);
        setNotes(r.data.company_notes || {});
      }).catch(() => {});
    } else {
      setForm({ type: 'other' });
      setReq({});
      setCustomReqs([]);
      setNotes({});
      setDocs([]);
    }
  }, [open, cemetery?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setReq((r) => ({ ...r, [k]: v }));

  const buildPayload = () => ({
    ...Object.fromEntries(Object.entries(form).filter(([k]) =>
      ['name', 'type', 'address', 'postcode', 'burial_authority', 'diocese', 'parish', 'telephone',
        'email', 'application_email', 'website', 'online_application_url', 'public_notes',
        'last_verified_date'].includes(k))),
    application_fee: form.application_fee === '' || form.application_fee == null ? null : parseFloat(form.application_fee),
    application_fee_inscription: form.application_fee_inscription === '' || form.application_fee_inscription == null ? null : parseFloat(form.application_fee_inscription),
    requirements: req,
    custom_requirements: customReqs.filter((c) => c.label),
  });

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Cemetery name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API}/cemeteries`, buildPayload(), auth);
        toast.success('Cemetery added and shared with the Memorial Desk database');
        onSaved?.(res.data);
        onOpenChange(false);
      } else if (suggestMode) {
        await axios.post(`${API}/cemeteries/${cemetery.id}/suggest-update`,
          { changes: buildPayload() }, auth);
        toast.success('Update suggested — Memorial Desk will review it');
        onOpenChange(false);
      } else {
        const res = await axios.put(`${API}/cemeteries/${cemetery.id}`, buildPayload(), auth);
        toast.success('Cemetery updated');
        onSaved?.(res.data);
        onOpenChange(false);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === 'duplicate_cemetery') {
        toast.info(`"${detail.existing_name}" already exists in the shared database — selecting it`);
        try {
          const r = await axios.get(`${API}/cemeteries/${detail.existing_id}`, auth);
          onSaved?.(r.data.cemetery);
        } catch {}
        onOpenChange(false);
      } else {
        toast.error(detail === 'suggest_update_required'
          ? 'This record is verified — please use Suggest Update instead'
          : 'Failed to save cemetery');
      }
    } finally { setSaving(false); }
  };

  const handleSaveNotes = async () => {
    try {
      await axios.put(`${API}/cemeteries/${cemetery.id}/company-notes`, notes, auth);
      toast.success('Private notes saved (only visible to your company)');
    } catch { toast.error('Failed to save notes'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('kind', uploadKind);
    fd.append('file', file);
    try {
      await axios.post(`${API}/cemeteries/${cemetery.id}/documents`, fd, auth);
      toast.success('Document uploaded');
      const r = await axios.get(`${API}/cemeteries/${cemetery.id}`, auth);
      setDocs(r.data.documents || []);
    } catch { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const openDoc = async (docId) => {
    try {
      const res = await axios.get(`${API}/cemeteries/${cemetery.id}/documents/${docId}/download`,
        { ...auth, responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Could not open document'); }
  };

  const deleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/cemeteries/${cemetery.id}/documents/${docId}`, auth);
      setDocs((d) => d.filter((x) => x.id !== docId));
      toast.success('Document removed');
    } catch { toast.error('Could not delete document (not uploaded by your company?)'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cemetery-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {isNew ? 'Add New Cemetery / Churchyard' : suggestMode ? `Suggest Update — ${cemetery.name}` : `Edit — ${cemetery.name}`}
          </DialogTitle>
          {suggestMode && (
            <DialogDescription className="text-xs text-amber-600">This record is trusted/verified. Your changes will be submitted as a suggestion for Memorial Desk review rather than applied immediately.</DialogDescription>
          )}
          {isNew && (
            <DialogDescription className="text-xs text-slate-500">Public details (name, contacts, fees, regulations) are shared with all Memorial Desk users. Your private notes and application history stay within your company.</DialogDescription>
          )}
          {!isNew && !suggestMode && (
            <DialogDescription className="text-xs text-slate-500">Public details are shared with all Memorial Desk users.</DialogDescription>
          )}
        </DialogHeader>

        <Tabs defaultValue="details">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details" data-testid="cemetery-tab-details">Details</TabsTrigger>
            <TabsTrigger value="requirements" data-testid="cemetery-tab-requirements">Requirements</TabsTrigger>
            <TabsTrigger value="documents" disabled={isNew} data-testid="cemetery-tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="notes" disabled={isNew} data-testid="cemetery-tab-notes">My Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-3 pt-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type || 'other'} onValueChange={(v) => set('type', v)}>
                <SelectTrigger data-testid="cemetery-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DETAIL_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <Input
                    value={form[k] ?? ''}
                    type={['application_fee', 'application_fee_inscription'].includes(k) ? 'number' : k === 'last_verified_date' ? 'date' : 'text'}
                    onChange={(e) => set(k, e.target.value)}
                    data-testid={`cemetery-field-${k}`}
                  />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs">Public notes</Label>
              <Textarea value={form.public_notes ?? ''} onChange={(e) => set('public_notes', e.target.value)} rows={2} data-testid="cemetery-field-public_notes" />
            </div>
            {!isNew && (
              <div className="flex gap-2 text-xs text-slate-500">
                <Badge variant="outline">{(cemetery.source || '').replace(/_/g, ' ')}</Badge>
                {cemetery.external_source_name && <Badge variant="outline">Source: {cemetery.external_source_name}</Badge>}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requirements" className="space-y-3 pt-3">
            <div>
              <Label className="text-xs">Memorial Desk application accepted</Label>
              <Select value={req.memorial_desk_application_accepted || 'unknown'}
                onValueChange={(v) => setR('memorial_desk_application_accepted', v)}>
                <SelectTrigger className="w-40" data-testid="req-md-accepted"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">Unknown</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {REQ_BOOL_FIELDS.map(([k, label]) => (
                <div key={k} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-slate-700">{label}</span>
                  <TriState value={req[k]} onChange={(v) => setR(k, v)} testId={`req-${k}`} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {REQ_TEXT_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <Input value={req[k] ?? ''} onChange={(e) => setR(k, e.target.value)} data-testid={`req-${k}`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs">Additional requirements</Label>
              <Textarea value={req.additional_requirements ?? ''} onChange={(e) => setR('additional_requirements', e.target.value)} rows={2} data-testid="req-additional_requirements" />
            </div>
            <div>
              <Label className="text-xs">Submission instructions</Label>
              <Textarea value={req.submission_instructions ?? ''} onChange={(e) => setR('submission_instructions', e.target.value)} rows={2} data-testid="req-submission_instructions" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Custom requirement fields</Label>
              {customReqs.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Requirement" value={c.label || ''} className="flex-1"
                    onChange={(e) => setCustomReqs((arr) => arr.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} />
                  <Input placeholder="Value / detail" value={c.value || ''} className="flex-1"
                    onChange={(e) => setCustomReqs((arr) => arr.map((x, xi) => xi === i ? { ...x, value: e.target.value } : x))} />
                  <Button variant="ghost" size="icon" onClick={() => setCustomReqs((arr) => arr.filter((_, xi) => xi !== i))}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setCustomReqs((a) => [...a, { label: '', value: '' }])} data-testid="add-custom-requirement-btn">
                + Add custom requirement
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-3 pt-3">
            <div className="flex items-center gap-2">
              <Select value={uploadKind} onValueChange={setUploadKind}>
                <SelectTrigger className="w-52" data-testid="doc-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="application_form">Authority application form</SelectItem>
                  <SelectItem value="regulations">Memorial regulations</SelectItem>
                  <SelectItem value="other">Other document</SelectItem>
                </SelectContent>
              </Select>
              <label className="cursor-pointer">
                <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,image/*" data-testid="doc-upload-input" />
                <span className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded-md hover:bg-slate-50">
                  <Upload className="w-4 h-4" /> Upload
                </span>
              </label>
            </div>
            {docs.length === 0 && <p className="text-sm text-slate-400">No documents uploaded yet.</p>}
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between border rounded-md px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{d.filename}</p>
                    <p className="text-xs text-slate-400">{(d.kind || '').replace(/_/g, ' ')} · {d.uploaded_by}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openDoc(d.id)} data-testid={`doc-view-${d.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => deleteDoc(d.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="notes" className="space-y-3 pt-3">
            <p className="text-xs text-slate-500">Private to your company — never shared with other Memorial Desk users.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {NOTE_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <Input value={notes[k] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [k]: e.target.value }))} data-testid={`note-${k}`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs">Internal notes / previous conversations</Label>
              <Textarea value={notes.internal_notes ?? ''} rows={4}
                onChange={(e) => setNotes((n) => ({ ...n, internal_notes: e.target.value }))} data-testid="note-internal_notes" />
            </div>
            <Button onClick={handleSaveNotes} className="bg-slate-700 hover:bg-slate-800" data-testid="save-private-notes-btn">
              Save Private Notes
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="cemetery-save-btn">
            {saving ? 'Saving…' : isNew ? 'Add Cemetery' : suggestMode ? 'Submit Suggestion' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
